// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import constants from "@store/constants";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import Link from "@store/MobiusOverrides/Link/Link";
import { formGridProps } from "@store/Themes/common/props/form";
import getMailChimpIsConfigured from "@store/Framework/Store/Store/Pages/UserSetup/Handlers/GetMailChimpIsConfigured";
import getMailChimpMemberInfo from "@store/Framework/Store/Store/Pages/UserSetup/Handlers/GetMailChimpMemberInfo";
import setMailChimpMemberInfo from "@store/Framework/Store/Store/Pages/UserSetup/Handlers/SetMailChimpMemberInfo";
import {
    loadMailChimpIsConfigured,
    loadMailChimpMemberInfo,
    loadMailChimpResponse,
} from "@store/Framework/Store/Store/Pages/UserSetup/StoreUserSetupSelectors";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { overrideSpacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser:
        getCurrentPage(state).type === constants.pageType.userSetup
            ? getCurrentEditingUser(state)
            : state.pages.accountSettings.editingAccount,
    isMailChimpConfigured: loadMailChimpIsConfigured(state),
    mailChimpMemberInfo: loadMailChimpMemberInfo(state),
    mailChimpResponse: loadMailChimpResponse(state),
});

const mapDispatchToProps = {
    getMailChimpIsConfigured,
    getMailChimpMemberInfo,
    setMailChimpMemberInfo,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupEmailPreferenceStyles {
    title?: TypographyPresentationProps;
    block?: BlockProps;
    innerBlock?: BlockProps;
    manageEmailPreferenceCheckbox?: CheckboxProps;
    manageInterestsCheckboxGroup?: CheckboxGroupPresentationProps;
    updateButton?: ButtonPresentationProps;
}

export const userSetupEmailPreferenceStyles: UserSetupEmailPreferenceStyles = {
    title: formGridProps.title,
    block: {
        variant: "separated",
    },
    innerBlock: {
        variant: "alternative",
        containerCss: css`
            ${overrideSpacingStyles(pxToRem(16), null)}
        `,
        wrapperCss: css`
            padding-left: ${pxToRem(16)};
            padding-right: ${pxToRem(16)};
        `,
    },
    manageInterestsCheckboxGroup: {
        css: css`
            ${overrideSpacingStyles(pxToRem(16), null)}
        `,
        mergeCss: true,
    },
};

const styles = userSetupEmailPreferenceStyles;

const UserSetupEmailPreference = ({
    editingUser,
    getMailChimpIsConfigured,
    getMailChimpMemberInfo,
    setMailChimpMemberInfo,
    isMailChimpConfigured,
    mailChimpMemberInfo,
    mailChimpResponse,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [showInterestCheckBoxGroup, setShowInterestCheckBoxGroup] = useState(mailChimpMemberInfo.isSubscribed);
    const [updatedMailChimpMemberInfo, setUpdatedMailChimpMemberInfo] = useState(mailChimpMemberInfo || undefined);

    useEffect(() => {
        if (mailChimpMemberInfo) {
            setUpdatedMailChimpMemberInfo(mailChimpMemberInfo);
            setShowInterestCheckBoxGroup(mailChimpMemberInfo.isSubscribed);
        }
    }, [mailChimpMemberInfo]);

    useEffect(() => {
        if (editingUser?.email) {
            getMailChimpIsConfigured({});
        }
    }, [editingUser?.email]);

    useEffect(() => {
        if (isMailChimpConfigured && editingUser?.email) {
            getMailChimpMemberInfo({ email: editingUser?.email });
        }
    }, [isMailChimpConfigured]);

    useEffect(() => {
        if (mailChimpResponse && mailChimpResponse.success) {
            toasterContext.addToast({
                body: translate("Your email preferences have been updated successfully"),
                messageType: "success",
            });
        } else if (mailChimpResponse && mailChimpResponse.success === false) {
            toasterContext.addToast({
                body: mailChimpResponse.message,
                messageType: "danger",
            });
        }
    }, [mailChimpResponse]);

    const emailPreferenceCheckboxHandler = () => {
        setShowInterestCheckBoxGroup(!showInterestCheckBoxGroup);
        setUpdatedMailChimpMemberInfo({ ...mailChimpMemberInfo, isSubscribed: !mailChimpMemberInfo.isSubscribed });
    };

    const interestCheckboxHandler = (id: string) => {
        const newInterestObj = updatedMailChimpMemberInfo.interests.map(interest => {
            if (interest.id === id) {
                const newInterest = { ...interest, isSubscribed: !interest.isSubscribed };
                return newInterest;
            }
            return interest;
        });
        setUpdatedMailChimpMemberInfo({ ...mailChimpMemberInfo, interests: newInterestObj });
    };

    const updateEmailPreferencesHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setMailChimpMemberInfo(updatedMailChimpMemberInfo);
    };

    return (
        <Block {...styles.block}>
            <Typography {...styles.title} data-test-selector="userSetupEmailPreference_title">
                {translate("Email Preferences")}
            </Typography>
            <Checkbox
                {...styles.manageEmailPreferenceCheckbox}
                checked={showInterestCheckBoxGroup}
                onChange={emailPreferenceCheckboxHandler}
                data-test-selector="userSetupEmailPreference_checkboxMain"
            >
                {translate("I wish to receive news from DCNE")}
            </Checkbox>
            {!showInterestCheckBoxGroup && updatedMailChimpMemberInfo.reSubscribeUrl && (
                <>
                    <Typography as="p" data-test-selector="userSetupEmailPreference_title">
                        {translate("You have unsubscribed to the news letter outside of the website.")}
                    </Typography>
                    <Link href={updatedMailChimpMemberInfo.reSubscribeUrl} target="_blank">
                        {translate("Please click here if you want to Resubscribe")}
                    </Link>
                </>
            )}
            {showInterestCheckBoxGroup &&
                !updatedMailChimpMemberInfo.reSubscribeUrl &&
                updatedMailChimpMemberInfo.interests && (
                    <Block {...styles.innerBlock}>
                        <Typography data-test-selector="userSetupEmailPreference_title">
                            {translate(
                                "Tell us which emails you'd like to receive from us. You can change these at any time.",
                            )}
                        </Typography>
                        <CheckboxGroup {...styles.manageInterestsCheckboxGroup}>
                            {updatedMailChimpMemberInfo.interests.map(interest => (
                                <Checkbox
                                    key={interest.id}
                                    uid={interest.id}
                                    checked={interest.isSubscribed}
                                    onChange={() => interestCheckboxHandler(interest.id)}
                                    data-test-selector="userSetupEmailPreference_checkboxPreferences"
                                >
                                    {interest.interestName}
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    </Block>
                )}
            <FormActions>
                <Button
                    onClick={updateEmailPreferencesHandler}
                    {...styles.updateButton}
                    type="submit"
                    data-test-selector="userSetupEmailPreference_updateButton"
                >
                    {translate("Update Preferences")}
                </Button>
            </FormActions>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupEmailPreference),
    definition: {
        group: "User Setup",
        displayName: "Email Preferences",
        allowedContexts: ["UserSetupPage", "AccountSettingsPage"],
    },
};

export default widgetModule;
